import { useMemo, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { textEntryIntentProps } from '@messenger/utils'
import Icon from 'components/priority/Icon/Icon'
import {
  EDITOR_DEFAULT_NAME_ORDER,
  EDITOR_FIELD_NAME_OF_DECEASED,
  EDITOR_NAME_PART_KEYS,
  editorCaseDataWithoutNameArrangement,
  editorCaseNameArrangement,
  isDefaultEditorNameParams,
  isEditorNamePart,
  editorTextBoxFieldLabel,
  editorTextBoxNamePartLabel,
  resolveEditorCompositeName,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCaseData,
  EditorNamePart,
  EditorSelection,
  EditorTextBoxMount,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { CaseFieldWrite } from '@/hooks/editor/useAuthorCaseField'
import {
  AUTHORING_PREVIEW_DEFAULT_STYLE,
  AuthoringFooter,
  AuthoringModalFrame,
  AuthoringPreviewCard,
  seedFieldRun,
  useAuthoringCommit,
} from './EditorTextBoxAuthoringModal'
import type { FieldAddress } from './EditorTextBoxAuthoringModal'
import * as S from './EditorTextBoxModal.styled'

const NAME_OF_DECEASED = EDITOR_FIELD_NAME_OF_DECEASED

type AuthorValues = (writes: readonly CaseFieldWrite[]) => Promise<boolean>

/** Every part once: an order that names only some parts gets the rest appended in the standard order. */
const completeNameOrder = (order: readonly string[]): EditorNamePart[] => [
  ...order.filter(isEditorNamePart),
  ...EDITOR_DEFAULT_NAME_ORDER.filter((part) => !order.includes(part)),
]

type NameSeed = {
  field: FieldAddress | null
  order: EditorNamePart[]
  visibility: Record<string, boolean>
}

type DeceasedNameFieldModalProps = {
  getMount: () => EditorTextBoxMount | null
  getHostEl: () => HTMLDivElement | null
  caseData?: EditorCaseData
  savedSel?: EditorSelection | null
  /** The name run the modal was opened on; unset, the box's first name run. */
  field?: FieldAddress | null
  onAuthorValues?: AuthorValues
  onClose: () => void
}

export const decomposeFlatName = (
  flat: string | null | undefined,
): { first?: string; last?: string } => {
  const tokens = String(flat ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (tokens.length === 0) return {}
  if (tokens.length === 1) return { first: tokens[0] }
  return {
    first: tokens.slice(0, -1).join(' '),
    last: tokens[tokens.length - 1],
  }
}

const DeceasedNameFieldModal = ({
  getMount,
  getHostEl,
  caseData = {},
  savedSel = null,
  field = null,
  onAuthorValues,
  onClose,
}: DeceasedNameFieldModalProps) => {
  const { t } = useTranslation()
  const nameLabel = editorTextBoxFieldLabel(t, EDITOR_FIELD_NAME_OF_DECEASED)
  // The modal opens on the case's arrangement when the case has one, else
  // on the run's own params (a case never arranged), else on the standard.
  const [seed] = useState<NameSeed>(() => {
    const fieldRun = seedFieldRun(getMount(), NAME_OF_DECEASED, field)
    const params =
      editorCaseNameArrangement(caseData) ??
      (fieldRun?.params as
        | { order?: string[]; visibility?: Record<string, boolean> }
        | undefined)
    return {
      field: fieldRun
        ? {
            blockIndex: fieldRun.blockIndex ?? 0,
            runIndex: fieldRun.runIndex,
          }
        : null,
      order: completeNameOrder(params?.order ?? EDITOR_DEFAULT_NAME_ORDER),
      visibility: { ...(params?.visibility ?? {}) },
    }
  })

  const [order, setOrder] = useState(seed.order)
  const [visibility, setVisibility] = useState(seed.visibility)

  const isPartVisible = (part: string) => visibility[part] !== false

  const togglePart = (part: string) =>
    setVisibility((prev) => ({ ...prev, [part]: prev[part] === false }))

  const movePart = (part: string, delta: -1 | 1) =>
    setOrder((prev) => {
      if (!isEditorNamePart(part)) return prev
      const from = prev.indexOf(part)
      const to = from + delta
      if (from < 0 || to < 0 || to >= prev.length) return prev
      const next = [...prev]
      next[from] = prev[to]
      next[to] = part
      return next
    })

  /** Puts `part` directly before or after `target` in the order. */
  const placePart = (part: string, target: string, before: boolean) =>
    setOrder((prev) => {
      if (part === target) return prev
      if (!isEditorNamePart(part) || !isEditorNamePart(target)) return prev
      const without = prev.filter((p) => p !== part)
      const at = without.indexOf(target)
      if (at < 0) return prev
      const next = [...without]
      next.splice(before ? at : at + 1, 0, part)
      return next.every((p, i) => p === prev[i]) ? prev : next
    })

  // The part being dragged by its grip and the row under the pointer, with
  // the half of that row the pointer is in; the release lands the part on
  // that side. Pointer events, not HTML5 drag-and-drop, so a touch screen
  // drags the same way a mouse does.
  const [drag, setDrag] = useState<{
    part: string
    over: string | null
    before: boolean
  } | null>(null)

  const rowUnderPointer = (
    handle: HTMLElement,
    clientY: number,
  ): { over: string; before: boolean } | null => {
    const list = handle.closest<HTMLElement>('[data-editor-text-name-list]')
    if (!list) return null
    const rows = Array.from(
      list.querySelectorAll<HTMLElement>('[data-name-row]'),
    )
    let nearest: { over: string; before: boolean; distance: number } | null =
      null
    for (const row of rows) {
      const rect = row.getBoundingClientRect()
      const mid = rect.top + rect.height / 2
      const distance =
        clientY < rect.top
          ? rect.top - clientY
          : clientY > rect.bottom
            ? clientY - rect.bottom
            : 0
      if (nearest === null || distance < nearest.distance)
        nearest = {
          over: row.getAttribute('data-name-row') ?? '',
          before: clientY < mid,
          distance,
        }
    }
    return nearest && { over: nearest.over, before: nearest.before }
  }

  const onHandlePointerDown =
    (part: string) => (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      setDrag({ part, over: null, before: true })
    }

  const onHandlePointerMove = (e: PointerEvent<HTMLElement>) => {
    if (!drag || !e.currentTarget.hasPointerCapture(e.pointerId)) return
    const hit = rowUnderPointer(e.currentTarget, e.clientY)
    if (hit && (drag.over !== hit.over || drag.before !== hit.before))
      setDrag({ ...drag, ...hit })
  }

  const onHandlePointerUp = (e: PointerEvent<HTMLElement>) => {
    if (!drag) return
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId)
    const hit = rowUnderPointer(e.currentTarget, e.clientY)
    if (hit) placePart(drag.part, hit.over, hit.before)
    setDrag(null)
  }

  const onHandleKeyDown = (part: string) => (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    movePart(part, e.key === 'ArrowUp' ? -1 : 1)
  }

  const resetParams = () => {
    setOrder([...EDITOR_DEFAULT_NAME_ORDER])
    setVisibility({})
  }

  const atDefaultParams = isDefaultEditorNameParams({ order, visibility })
  const [partValues, setPartValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {}
    for (const part of Object.keys(EDITOR_NAME_PART_KEYS))
      v[part] = caseData[EDITOR_NAME_PART_KEYS[part]] ?? ''
    const anyPart = Object.keys(EDITOR_NAME_PART_KEYS).some(
      (p) => v[p].trim() !== '',
    )
    if (!anyPart)
      Object.assign(v, decomposeFlatName(caseData[NAME_OF_DECEASED]))
    return v
  })

  const { saving, error, run } = useAuthoringCommit()

  const partsDirty = Object.keys(EDITOR_NAME_PART_KEYS).some(
    (part) =>
      (partValues[part] ?? '') !==
      (caseData[EDITOR_NAME_PART_KEYS[part]] ?? ''),
  )
  // The preview shows the modal's own order and visibility, so the case's
  // arrangement keys are left out of the data it resolves against.
  const previewCase = useMemo(
    () => editorCaseDataWithoutNameArrangement(caseData),
    [caseData],
  )
  const draftPartsOnly = useMemo<EditorCaseData>(() => {
    const d: Record<string, string> = {
      ...previewCase,
      [NAME_OF_DECEASED]: '',
    }
    for (const part of Object.keys(EDITOR_NAME_PART_KEYS))
      d[EDITOR_NAME_PART_KEYS[part]] = partValues[part] ?? ''
    return d
  }, [previewCase, partValues])

  const previewText = partsDirty
    ? resolveEditorCompositeName(draftPartsOnly, { order, visibility })
    : resolveEditorCompositeName(previewCase, { order, visibility })

  const restoreSelection = () => {
    getHostEl()?.focus()
    if (savedSel) getMount()?.placeRangeAtPos(savedSel.anchor, savedSel.focus)
  }

  const cancel = () => {
    restoreSelection()
    onClose()
  }

  const paramsDirty =
    order.length !== seed.order.length ||
    order.some((p, i) => p !== seed.order[i]) ||
    Object.keys({ ...seed.visibility, ...visibility }).some(
      (part) =>
        (seed.visibility[part] !== false) !== (visibility[part] !== false),
    )

  /**
   * One case write for everything the modal changed: the part values that
   * differ from the case, and the arrangement when the order or visibility
   * moved from what the modal opened on. The arrangement is the case's,
   * for the name boxes of both editors built from name parts
   * (`docs/features/text-editor.md` names the exceptions); Reset writes the
   * standard arrangement explicitly, never nothing.
   */
  const commitCase = async (): Promise<boolean> => {
    if (!onAuthorValues) return true
    const writes: CaseFieldWrite[] = Object.keys(EDITOR_NAME_PART_KEYS).flatMap(
      (part) => {
        const key = EDITOR_NAME_PART_KEYS[part]
        const next = partValues[part] ?? ''
        return next !== (caseData[key] ?? '')
          ? [{ fieldKey: key, value: next }]
          : []
      },
    )
    if (paramsDirty)
      writes.push({
        fieldKey: NAME_OF_DECEASED,
        arrangement: {
          order: [...order],
          hidden: order.filter((part) => visibility[part] === false),
        },
      })
    if (writes.length === 0) return true
    return run(
      () => onAuthorValues(writes),
      t('editor.textBox.modals.name.saveError'),
    )
  }

  const insertName = async () => {
    if (!(await commitCase())) return
    restoreSelection()
    getMount()?.insertField(NAME_OF_DECEASED)
    onClose()
  }

  /** The insert modal's other commit: the case takes the parts and the arrangement, the box takes nothing. */
  const saveToCase = async () => {
    if (!(await commitCase())) return
    cancel()
  }

  const saveName = async () => {
    if (!(await commitCase())) return
    cancel()
  }

  const detachName = () => {
    if (seed.field)
      getMount()?.convertFieldToText(seed.field.blockIndex, seed.field.runIndex)
    cancel()
  }

  const editMode = seed.field !== null

  return (
    <AuthoringModalFrame
      backdropTestId="data-editor-text-name-modal-backdrop"
      onDismiss={cancel}
      dialogAttrs={{ 'data-editor-text-name-modal': true }}
      ariaLabel={nameLabel}
      title={nameLabel}
      subtitle={
        editMode
          ? t('editor.textBox.modals.name.subtitleEdit')
          : t('editor.textBox.modals.name.subtitleInsert')
      }
      closeAttrs={{ 'data-editor-text-name-close': true }}
      footer={
        <>
          {error && (
            <S.ErrorText data-editor-text-name-error>{error}</S.ErrorText>
          )}
          <AuthoringFooter
            danger={
              editMode ? (
                <S.DangerBtn
                  type="button"
                  data-editor-text-name-detach
                  title={t('editor.textBox.modals.name.convertTitle')}
                  onClick={detachName}
                >
                  {t('editor.textBox.modals.common.convertToText')}
                </S.DangerBtn>
              ) : null
            }
            cancelAttrs={{ 'data-editor-text-name-cancel': true }}
            onCancel={cancel}
            secondary={
              editMode || !onAuthorValues
                ? null
                : {
                    attrs: { 'data-editor-text-name-save-only': true },
                    label: t('editor.textBox.modals.common.saveToCase'),
                    title:
                      partsDirty || paramsDirty
                        ? t('editor.textBox.modals.name.saveOnlyTitle')
                        : t('editor.textBox.modals.name.unchangedTitle'),
                    disabled: !partsDirty && !paramsDirty,
                    onClick: saveToCase,
                  }
            }
            primaryAttrs={
              editMode
                ? { 'data-editor-text-name-save': true }
                : { 'data-editor-text-name-insert': true }
            }
            primaryLabel={
              editMode
                ? t('editor.textBox.modals.common.saveChanges')
                : t('editor.textBox.modals.name.insertName')
            }
            onPrimary={editMode ? saveName : insertName}
            saving={saving}
          />
        </>
      }
    >
      <S.Section>
        <S.SectionLabel>
          {t('editor.textBox.modals.common.preview')}
        </S.SectionLabel>
        <AuthoringPreviewCard
          effective={AUTHORING_PREVIEW_DEFAULT_STYLE}
          text={previewText}
          ghost={t('editor.textBox.modals.name.ghost', { label: nameLabel })}
          cardAttrs={{ 'data-editor-text-name-preview': true }}
          textAttrs={{ 'data-editor-text-name-preview-text': true }}
          ghostAttrs={{ 'data-editor-text-name-preview-ghost': true }}
        />
      </S.Section>

      <S.Section>
        <S.SectionLabel>
          {t('editor.textBox.modals.name.partsHeading')}
        </S.SectionLabel>
        <S.PartList data-editor-text-name-list>
          {order.map((part) => {
            const visible = isPartVisible(part)
            const label = editorTextBoxNamePartLabel(t, part)
            const dropSide =
              drag && drag.over === part && drag.part !== part
                ? drag.before
                  ? 'before'
                  : 'after'
                : null
            return (
              <S.PartRow
                key={part}
                data-name-row={part}
                data-name-drop={dropSide ?? undefined}
                $hidden={!visible}
                $dragging={drag?.part === part}
                $dropSide={dropSide}
              >
                <S.PartDragHandle
                  tabIndex={0}
                  role="button"
                  data-name-drag={part}
                  aria-label={t('editor.textBox.modals.name.dragAria', {
                    label,
                  })}
                  title={t('editor.textBox.modals.name.dragTitle', { label })}
                  onPointerDown={onHandlePointerDown(part)}
                  onPointerMove={onHandlePointerMove}
                  onPointerUp={onHandlePointerUp}
                  onPointerCancel={() => setDrag(null)}
                  onKeyDown={onHandleKeyDown(part)}
                >
                  <Icon icon="bars2Icon" width="14px" height="14px" />
                </S.PartDragHandle>
                <S.PartToggle
                  type="checkbox"
                  data-name-visible={part}
                  checked={visible}
                  aria-label={t('editor.textBox.modals.name.showAria', {
                    label,
                  })}
                  title={
                    visible
                      ? t('editor.textBox.modals.name.printedTitle', { label })
                      : t('editor.textBox.modals.name.leftOutTitle', { label })
                  }
                  onChange={() => togglePart(part)}
                />
                <S.PartLabel $hidden={!visible}>{label}</S.PartLabel>
                <S.PartValueInput
                  data-name-value={part}
                  {...textEntryIntentProps()}
                  type="text"
                  value={partValues[part] ?? ''}
                  placeholder="—"
                  title={t('editor.textBox.modals.name.valueTitle', { label })}
                  onChange={(e) =>
                    setPartValues((v) => ({ ...v, [part]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      e.stopPropagation()
                      if (editMode) saveName()
                      else insertName()
                    }
                  }}
                />
              </S.PartRow>
            )
          })}
        </S.PartList>
        <S.PartResetBtn
          type="button"
          data-editor-text-name-reset
          disabled={atDefaultParams}
          title={t('editor.textBox.modals.name.resetTitle')}
          onClick={resetParams}
        >
          {t('editor.textBox.modals.name.reset')}
        </S.PartResetBtn>
      </S.Section>
    </AuthoringModalFrame>
  )
}

export default DeceasedNameFieldModal
